/**
 * 書式のエディタ（帳票デザインの2回目・3回目、#78、docs/31 §9）。/settings/paper/edit
 *   ・?id=<書式の id> … 自分の書式を開いて直す
 *   ・?kind=quote|invoice|receipt&slot=1..3&from=preset:<key> … プリセットから起こして、その枠に保存する（保存するまで DB には無い）
 *   ・3回目（046）：画像（画像タブから置く・下敷き）、二次元コード、テスト印刷とプレビューを本物の見積・請求書・領収書の中身で
 *   ・パソコン（幅 1100px 以上）だけ。管理者だけ保存できる（担当者は見るだけ）。オプションの門は API（Me.features.paperDesign）
 *   ・上の帯：名前・元に戻す／やり直す・テスト印刷（PDF）・保存。左：紙。右：部品／明細表／用紙／色。下：警告
 *   ・矢印キーで 1mm（Shift で 5mm）、Delete で消す、Ctrl/⌘+Z で元に戻す（入力欄にいるときは効かない）
 */
import { MSG, PAPER_KIND_NAME, PAPER_LAYOUT_MSG, PAPER_PRESETS, PRODUCTS, PaperKind, PaperPresetKey, type PaperData, type PaperDocList, type PaperImageList, type PaperLayout, type PaperLayoutView, type PaperPresetKey as PresetKeyT } from "@genbaryoku/contract";
import { WARN_KIND_LABEL, applyLocks, layoutFromPreset, layoutWarningItems, paperGeometry, r1, sampleData } from "@genbaryoku/paper";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { api, fetchFile, messageOf } from "../../api.js";
import { writeMaking } from "../../lib/pdfOpen.js";
import { useMe } from "../../auth.js";
import { BackButton, useBackNav, usePageName } from "../../components/BackButton.jsx";
import { ErrorText, Help, useToast } from "../../components/ui.jsx";
import { makeEl, makeImageEl, makeQrEl, updateEl, useHistory, type Sel } from "./editorState.js";
import { currentLabels, t } from "../../labels.js";
import { PaperCanvas } from "./PaperCanvas.jsx";
import { Pop } from "../schedule/Overlap.jsx";
import { IMAGES_QUERY, PaperPane, assetUrl, type Tab } from "./PaperPane.jsx";

const KIND_NAME = PAPER_KIND_NAME;

export function PaperEditorPage() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const me = useMe();
  const admin = me.data?.user.role === "admin";
  const available = me.data?.features.paperDesign !== false;
  const { toast, node } = useToast();
  const id = sp.get("id");
  const kindQ = PaperKind.safeParse(sp.get("kind")).success ? (sp.get("kind") as PaperKind) : "quote";
  const slotQ = Math.min(3, Math.max(1, Number(sp.get("slot") ?? "1") || 1));
  const fromQ = sp.get("from") ?? "preset:std-p";

  const [wide, setWide] = useState(() => window.innerWidth >= 1100);
  useEffect(() => {
    const f = () => setWide(window.innerWidth >= 1100);
    window.addEventListener("resize", f);
    return () => window.removeEventListener("resize", f);
  }, []);

  // 既存の書式を開く
  const existing = useQuery({ queryKey: ["paper-layout", id], queryFn: () => api<PaperLayoutView>("GET", `/paper/layouts/${id}`), enabled: !!id });
  const kind = existing.data?.layout.kind ?? kindQ;
  const slot = existing.data?.slot ?? slotQ;
  /** 帳票の名前（{order}は会社の言葉。#233 2d） */
  const kindName = t(KIND_NAME[kind]);
  /** 建設の会社の{order}の書式は 工事名・「記載のない事項は…」の1行も消せない（#233 2d。API も 400） */
  const lockOpt = { construction: kind === "order" && !!me.data?.tenant.modules?.includes("field") };
  // 見本のデータか、本物の帳票の中身（046）。{order}は兼請書の書式なら請書の欄（署名欄）も（#233 2d）
  const [docId, setDocId] = useState("");
  const h = useHistory(null);
  const { layout, commit, reset, undo, redo, dirty } = h;
  const combinedQ = kind === "order" && !!layout?.combined;
  const data = useQuery({ queryKey: ["paper-data", kind, docId, combinedQ], queryFn: () => api<PaperData>("GET", `/paper/data?kind=${kind}${docId ? `&docId=${docId}` : ""}${combinedQ ? "&combined=1" : ""}`) });
  const docs = useQuery({ queryKey: ["paper-docs", kind], queryFn: () => api<PaperDocList>("GET", `/paper/docs?kind=${kind}`) });
  // 画像（046）：書式が指す画像の URL（エディタは API から <img> で読む。会社のロゴ・社印は登録があるときだけ）
  const images = useQuery({ queryKey: IMAGES_QUERY, queryFn: () => api<PaperImageList>("GET", "/paper/images") });

  const construction = lockOpt.construction;
  useEffect(() => {
    if (id) {
      if (existing.data) reset(applyLocks(existing.data.layout, { construction }));
      return;
    }
    const key = PaperPresetKey.safeParse(fromQ.startsWith("preset:") ? fromQ.slice(7) : fromQ);
    // 紙に刷る見出しは会社の言葉（#142 段6 No.32・33）
    reset(applyLocks(layoutFromPreset(kind, (key.success ? key.data : "std-p") as PresetKeyT, undefined, currentLabels()), { construction }));
  }, [id, existing.data, kind, fromQ, reset, construction]);

  const [sel, setSel] = useState<Sel>(null);
  const [tab, setTab] = useState<Tab>("els");
  const [colorTarget, setColorTarget] = useState("table.color");
  const [zoom, setZoom] = useState<"fit" | "50" | "75" | "100" | "150">("fit");
  const [snap, setSnap] = useState(1);
  const [grid, setGrid] = useState(false);
  const [groupMove, setGroupMove] = useState(true);
  const [editing, setEditing] = useState<string | null>(null);

  const select = useCallback((s: Sel) => {
    setSel(s);
    if (s && tab !== "els" && tab !== "color") setTab("els");
  }, [tab]);

  /** 製品ごと（#142 段6 No.36〜38）：見本の中身と文字の部品の定型文 */
  const product = me.data?.tenant.product ?? "genba";
  const labelsNow = currentLabels();
  const sample = data.data ?? sampleData(kind, 7, product, { combined: combinedQ, names: { order: labelsNow.order, orderAck: labelsNow.orderAck } });
  /**
   * 紙に描く中身（#217 の本番の確かめ）：見積書・請求書で本物の帳票を選んだときは、PDF の1ページ目と同じ表紙の形
   * （内訳1 … 別紙 (P. n)）。書式に内訳の紙の設定が無くても（#217 より前のカスタム書式＝PDF と同じ。#217 の不具合）。見本と領収書は sample のまま
   */
  const coverForm = !!docId && kind !== "receipt";
  const coverData = useQuery({ queryKey: ["paper-data", kind, docId, "sections"], queryFn: () => api<PaperData>("GET", `/paper/data?kind=${kind}&docId=${docId}&sections=1`), enabled: coverForm });
  const shown = coverForm ? (coverData.data ?? sample) : sample;
  /** 注意（#217 ■4）：重なり・紙の端・はみ出し（見本か本物の中身で）・薄い色。はみ出しは内訳の行も見る（1枚に続けた中身と、紙に描く表紙の両方） */
  const warnings = useMemo(() => {
    if (!layout) return [];
    const all = layoutWarningItems(layout, sample, lockOpt);
    if (shown === sample) return all;
    const seen = new Set(all.map((w) => w.text));
    return [...all, ...layoutWarningItems(layout, shown, lockOpt).filter((w) => !seen.has(w.text))];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout, sample, shown, construction]);
  const warnIds = useMemo(() => new Set(warnings.flatMap((w) => [w.target, w.other]).filter((x): x is string => !!x)), [warnings]);
  const geom0 = useMemo(() => (layout ? paperGeometry(layout, shown) : null), [layout, shown]);
  const dockBaseY = geom0?.dockBase ?? 0;
  /** 備考の下に付ける部品（#233 2d）の基準 */
  const endBaseY = geom0?.endBase ?? dockBaseY;
  /** 枠（#217 ■4）：全部の部品の外枠を点線で（既定 ON・この端末に覚える） */
  const [frames, setFramesRaw] = useState(() => {
    try { return localStorage.getItem("gb.pe.frames") !== "0"; } catch { return true; }
  });
  const setFrames = (v: boolean) => {
    setFramesRaw(v);
    try { localStorage.setItem("gb.pe.frames", v ? "1" : "0"); } catch { /* 覚えられなくても動く */ }
  };
  /** 注意の行を押す：その部品を選び、紙の上で見える所まで寄せる */
  const goWarn = (id: string | null) => {
    if (!id) return;
    select(id);
    requestAnimationFrame(() => document.querySelector(`[data-testid="paper-canvas"] [data-id="${CSS.escape(id)}"]`)?.scrollIntoView({ block: "center", inline: "center", behavior: "smooth" }));
  };
  /** プリセットから作り直す（#217）：名前と枠の番号は残す。#216 より前の形で保存した書式を、今の紙の形にするため */
  const rebuild = (key: PresetKeyT) => {
    if (!layout) return;
    commit({ ...applyLocks(layoutFromPreset(kind, key, layout.name, currentLabels()), lockOpt), name: layout.name });
    setSel(null);
    toast(`「${t(PAPER_PRESETS.find((p) => p.key === key)?.name ?? key)}」から作り直しました（↶ で戻せます）。保存するまで書式は変わりません`);
  };
  /** 「注意 n」のチップ（#171。前は紙の下の警告の列）：押すと一覧、開いているあいだ該当の部品を朱の点線で囲む */
  const [warnAt, setWarnAt] = useState<HTMLElement | null>(null);
  const marks = useMemo(() => (warnAt ? [...new Set(warnings.map((w) => w.target).filter((x): x is string => !!x))] : undefined), [warnAt, warnings]);
  useEffect(() => {
    if (!warnings.length) setWarnAt(null);
  }, [warnings.length]);
  const assets = useMemo(() => {
    const out: Record<string, string> = {};
    if (!layout) return out;
    const co = images.data?.company;
    const known = new Set((images.data?.items ?? []).map((x) => x.id));
    for (const e of layout.els) {
      if (e.type !== "image" || !e.asset) continue;
      if (e.asset === "company:logo") { if (co?.logo) out[e.asset] = assetUrl(e.asset); continue; }
      if (e.asset === "company:seal") { if (co?.seal) out[e.asset] = assetUrl(e.asset); continue; }
      if (e.asset === "site:map") continue; // #133 の現場の地図（#137 で取り外し）：古い書式に残っていても絵は入れない
      if (known.has(e.asset)) out[e.asset] = assetUrl(e.asset);
    }
    return out;
  }, [layout, images.data]);

  /* ---- 部品を足す・複製・消す ---- */
  const add = (k: "text" | "field" | "line" | "vline" | "box" | "qr", text?: string) => {
    if (!layout) return;
    const el = k === "qr" ? makeQrEl(layout) : makeEl(layout, k, text);
    commit({ ...layout, els: [...layout.els, el] });
    setSel(el.id);
    setTab("els");
    if (k === "text" && !text) setTimeout(() => setEditing(el.id), 0);
  };
  const addImage = (asset: string, name: string, px: { width: number; height: number } | null, guide: boolean) => {
    if (!layout) return;
    const el = makeImageEl(layout, asset, guide ? `下敷き：${name}` : name, px, guide);
    // 下敷きはいちばん下（先頭）に、ほかは上（末尾）に
    commit({ ...layout, els: guide ? [el, ...layout.els] : [...layout.els, el] });
    setSel(el.id);
    setTab("els");
    toast(guide ? "下敷きを敷きました（印刷には出ません）。部品を枠に合わせてください" : `「${name}」を置きました`);
  };
  const duplicate = () => {
    if (!layout || !sel || sel === "table") return;
    const el = layout.els.find((e) => e.id === sel);
    if (!el) return;
    const c = { ...el, id: makeEl(layout, "text").id, name: `${el.name}（写し）`, locked: false, x: r1(el.x + 5), ...(el.dock ? { dy: r1((el.dy ?? 0) + 5) } : { y: r1(el.y + 5) }) };
    commit({ ...layout, els: [...layout.els, c] });
    setSel(c.id);
  };
  const remove = () => {
    if (!layout || !sel || sel === "table") return;
    const el = layout.els.find((e) => e.id === sel);
    if (!el || el.locked) return;
    commit({ ...layout, els: layout.els.filter((e) => e.id !== sel) });
    setSel(null);
    toast(`「${el.name}」を消しました（↶ で戻せます）`);
  };

  /* ---- キー操作 ---- */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (document.activeElement as HTMLElement | null)?.tagName ?? "";
      if (["INPUT", "SELECT", "TEXTAREA"].includes(tag) || editing) return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") { e.preventDefault(); if (e.shiftKey) redo(); else undo(); return; }
      if (e.key === "Delete" || e.key === "Backspace") { if (sel && sel !== "table") { e.preventDefault(); remove(); } return; }
      const k = ({ ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] } as Record<string, [number, number]>)[e.key];
      if (!k || !sel || !layout) return;
      e.preventDefault();
      const s = e.shiftKey ? 5 : 1;
      if (sel === "table") { commit({ ...layout, table: { ...layout.table, x: r1(layout.table.x + k[0] * s), y: r1(layout.table.y + k[1] * s) } }); return; }
      const el = layout.els.find((x) => x.id === sel);
      if (!el) return;
      const ids = new Set((groupMove && el.group ? layout.els.filter((x) => x.group === el.group) : [el]).map((x) => x.id));
      commit({ ...layout, els: layout.els.map((o) => (ids.has(o.id) ? { ...o, x: r1(o.x + k[0] * s), ...(o.dock ? { dy: r1((o.dy ?? 0) + k[1] * s) } : { y: r1(o.y + k[1] * s) }) } : o)) });
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });

  /* ---- 保存・テスト印刷 ---- */
  const save = useMutation({
    mutationFn: (L: PaperLayout) => api<PaperLayoutView>("PUT", `/paper/layouts/${kind}/${slot}`, { layout: L }),
    onSuccess: (v) => {
      reset(v.layout);
      qc.invalidateQueries({ queryKey: ["paper-layouts"] });
      qc.invalidateQueries({ queryKey: ["paper-presets"] });
      qc.setQueryData(["paper-layout", v.id], v);
      toast(`「${v.layout.name}」を${kindName}のカスタム書式 ${v.slot} に保存しました。この書式を選んだ次の帳票から変わります`);
      if (!id) nav(`/settings/paper/edit?id=${v.id}`, { replace: true });
    },
    onError: (e) => toast(messageOf(e), undefined, "error"),
  });
  const [testing, setTesting] = useState(false);
  // テスト印刷：押した瞬間に新しいタブを開いて「PDF 作成中…」、できたら差し替える（取ってから開くと Safari が止める）。
  // 作成中は押せない（PDF はサーバーで順番を待つことがある。docs/72 §10）
  const testPrint = async () => {
    if (!layout || testing) return;
    setTesting(true);
    const w = window.open("", "_blank");
    if (w) writeMaking(w);
    try {
      const blob = await fetchFile("/api/v1/paper/test-pdf", { method: "POST", body: { layout, ...(docId ? { docId } : {}) } });
      const url = URL.createObjectURL(blob);
      if (w && !w.closed) w.location.href = url;
      else if (!w) window.open(url, "_blank");
    } catch (e) {
      w?.close();
      toast(messageOf(e), undefined, "error");
    } finally {
      setTesting(false);
    }
  };

  // 保存していない直しがあるときの離脱の確かめ
  useEffect(() => {
    if (!dirty) return;
    const f = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener("beforeunload", f);
    return () => window.removeEventListener("beforeunload", f);
  }, [dirty]);

  // 戻る（#98、docs/37）：直前の画面があればそこ。無ければ 帳票デザイン。保存していない直しは確かめる（前の window.confirm から）
  const back = useBackNav({ to: "/settings/paper", label: "帳票デザイン" }, dirty);
  usePageName(`${kindName} カスタム書式 ${slot}`);
  if (!wide) return <><h1>帳票デザイン</h1><p className="readonly-note">{PAPER_LAYOUT_MSG.pcOnly}</p><p><Link className="btn" to="/settings/paper">帳票デザインへ</Link></p></>;
  if (existing.isError) return <ErrorText>{messageOf(existing.error)}</ErrorText>;
  if (!layout) return <p className="hint">読み込んでいます…</p>;

  return (
    <div className="pe" data-testid="paper-editor">
      <div className="pe-bar">
        <BackButton back={back} />
        {back.dialog}
        <span className="pe-kind">{kindName} カスタム書式 {slot}</span>
        <label className="pe-f h"><span>名前</span><input className="inp" type="text" style={{ width: 170 }} value={layout.name} data-testid="pe-name" onChange={(e) => commit({ ...layout, name: e.target.value })} maxLength={40} /></label>
        {dirty ? <span className="pe-tag red">未保存</span> : null}
        {layout.page.preprinted ? <span className="pe-tag">専用帳票モード</span> : null}
        {warnings.length ? (
          <button type="button" className="chip shu pe-warnchip" aria-haspopup="dialog" aria-expanded={!!warnAt} title="押すと一覧（該当の部品を枠で囲みます）" onClick={(e) => setWarnAt(warnAt ? null : e.currentTarget)} data-testid="pe-warns">
            注意 {warnings.length}
          </button>
        ) : (
          <span className="chip ai pe-warnchip" title="問題はありません" data-testid="pe-warns">
            ✓ 問題なし
          </span>
        )}
        {warnAt ? (
          <Pop anchor={warnAt} onClose={() => setWarnAt(null)} label="注意" testId="pe-warn-list" wide>
            <ul className="pe-warnlist">
              {warnings.map((w, i) => {
                const body = (
                  <>
                    <span className="pe-wk">{WARN_KIND_LABEL[w.kind]}</span>
                    <span className="pe-wn">{w.name}</span>
                    <span className="pe-wo">{w.otherName ? `× ${w.otherName}` : ""}</span>
                    <span className="pe-wm">{w.mm != null ? `${w.mm}mm` : ""}</span>
                    <span className="pe-wt">{w.text}</span>
                  </>
                );
                return (
                  <li key={i}>
                    {w.target ? (
                      <button type="button" onClick={() => goWarn(w.target)} data-testid="pe-warn-item" data-kind={w.kind}>{body}</button>
                    ) : (
                      <span data-testid="pe-warn-item" data-kind={w.kind}>{body}</span>
                    )}
                  </li>
                );
              })}
            </ul>
          </Pop>
        ) : null}
        <label className="pe-cb" title="全部の部品の外枠を点線で出します（印刷には出ません）"><input type="checkbox" checked={frames} onChange={(e) => setFrames(e.target.checked)} data-testid="pe-frames-toggle" />枠</label>
        <span className="sp" />
        <button type="button" className="btn quiet sq" onClick={undo} disabled={!h.canUndo} title="元に戻す（Ctrl+Z）" aria-label="元に戻す">↶</button>
        <button type="button" className="btn quiet sq" onClick={redo} disabled={!h.canRedo} title="やり直す（Shift+Ctrl+Z）" aria-label="やり直す">↷</button>
        <label className="pe-f h"><span>中身</span>
          <select className="inp pe-sel" value={docId} aria-label="中身" data-testid="pe-doc" onChange={(e) => setDocId(e.target.value)} style={{ maxWidth: 190 }}>
            <option value="">見本のデータ</option>
            {(docs.data?.items ?? []).map((d) => <option key={d.id} value={d.id}>{d.no || "（下書き）"} {d.title || "（件名なし）"}{d.client ? `／${d.client}` : ""}</option>)}
          </select>
        </label>
        {/* プリセットから作り直す（#217）：選ぶとその形で部品を置き直す（名前と書式の番号は残る。↶ で戻せる。保存するまで変わらない） */}
        <select className="inp pe-sel" aria-label="プリセットから作り直す" value="" data-testid="pe-rebuild" title="選んだプリセットの形で部品を置き直します。書式の名前と番号はそのままです（↶ で戻せます）" onChange={(e) => { if (e.target.value) rebuild(e.target.value as PresetKeyT); }} style={{ maxWidth: 200 }}>
          <option value="">プリセットから作り直す…</option>
          {PAPER_PRESETS.filter((p) => p.kinds.includes(kind)).map((p) => <option key={p.key} value={p.key}>{t(p.name)}</option>)}
        </select>
        <button type="button" className="btn" onClick={() => void testPrint()} disabled={testing} aria-busy={testing} data-testid="pe-test">{testing ? MSG.pdfMaking : "テスト印刷（PDF）"}</button>
        <button type="button" className="btn primary" onClick={() => save.mutate(layout)} disabled={!admin || !available || save.isPending} data-testid="pe-save" title={!admin ? "保存できるのは管理者だけです" : !available ? PAPER_LAYOUT_MSG.optionOff : ""}>保存</button>
        <Help text={admin ? "保存すると、この書式を選んだ次の帳票から変わります。すでに出した見積・請求書は変わりません。書式は見積書・請求書とも3つまでです。" : "書式の保存と会社の既定の変更は管理者だけができます。担当者は見ることができます。"} />
      </div>
      <div className="pe-toolbar">
        <span className="seg sm" role="group" aria-label="部品を追加">
          <button type="button" onClick={() => add("text")} data-testid="pe-add-text">＋文字</button>
          <button type="button" onClick={() => add("field")} data-testid="pe-add-field">＋項目</button>
          <button type="button" onClick={() => add("line")}>＋横線</button>
          <button type="button" onClick={() => add("vline")}>＋縦線</button>
          <button type="button" onClick={() => add("box")}>＋枠</button>
          <button type="button" onClick={() => setTab("images")} data-testid="pe-add-image">＋画像</button>
          <button type="button" onClick={() => add("qr")} data-testid="pe-add-qr">＋二次元コード</button>
        </span>
        <select className="inp pe-sel" aria-label="定型文を追加" value="" onChange={(e) => { if (e.target.value) add("text", e.target.value); }}>
          <option value="">定型文を追加…</option>
          {PRODUCTS[product].paperPhrases.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <span className="sp" />
        <label className="pe-f h"><span>表示</span>
          <select className="inp pe-sel" value={zoom} aria-label="表示の大きさ" onChange={(e) => setZoom(e.target.value as typeof zoom)}>
            <option value="fit">幅に合わせる</option><option value="50">50%</option><option value="75">75%</option><option value="100">100%（実寸）</option><option value="150">150%</option>
          </select>
        </label>
        <label className="pe-f h"><span>吸着</span>
          <select className="inp pe-sel" value={String(snap)} aria-label="吸着" onChange={(e) => setSnap(Number(e.target.value))}>
            <option value="0">無し</option><option value="1">1mm</option><option value="2.5">2.5mm</option><option value="5">5mm</option>
          </select>
        </label>
        <label className="pe-cb"><input type="checkbox" checked={grid} onChange={(e) => setGrid(e.target.checked)} />方眼</label>
        <label className="pe-cb"><input type="checkbox" checked={groupMove} onChange={(e) => setGroupMove(e.target.checked)} data-testid="pe-group" />見出しと値を組で動かす</label>
        <Help text="「＋文字」で好きな文字（注意書き・見出し・「御中」など）を用紙に置けます。置いたらそのまま打ち、Enter で決定（改行は Shift+Enter）。あとで修正するときは用紙の上の文字をダブルクリック。部品はドラッグで動かせます。角や辺の四角をドラッグすると大きさが変わります。数字で決めたいときは右の「部品」タブに mm で打ちます。矢印キーで 1mm、Shift で 5mm。「組で動かす」を外すと、見出しと値を別々に動かせます。「＋画像」は右の画像タブから置きます（ロゴ・印影・下敷き）。「中身」を本物の見積にすると、その見積の内容で帳票を確かめられます。朱の点線（用紙の端 5mm）と藍の点線（余白）は目安で、印刷には出ません。" />
      </div>
      <div className="pe-body">
        <div className="pe-left">
          <PaperCanvas
            layout={layout}
            data={shown}
            sel={sel}
            onSelect={select}
            onChange={(next) => commit(next)}
            zoom={zoom}
            snap={snap}
            grid={grid}
            groupMove={groupMove}
            editing={editing}
            assets={assets}
            marks={marks}
            frames={frames}
            warnings={warnings}
            onEditStart={(eid) => setEditing(eid)}
            onEditEnd={(eid, text) => {
              setEditing(null);
              if (text === null) return;
              commit((cur) => updateEl(cur, eid, (e) => (e.type === "text" ? { ...e, text, name: e.name === "文字" || e.name.endsWith("（写し）") ? (text.split("\n")[0] || "文字").slice(0, 12) : e.name } : e)));
            }}
          />
        </div>
        <PaperPane
          layout={layout}
          sel={sel}
          onSelect={select}
          commit={commit}
          tab={tab}
          setTab={setTab}
          colorTarget={colorTarget}
          setColorTarget={setColorTarget}
          onAdd={(k) => add(k)}
          onAddImage={addImage}
          onDuplicate={duplicate}
          onDelete={remove}
          toast={toast}
          admin={!!admin}
          warnIds={warnIds}
          dockBaseY={dockBaseY}
          endBaseY={endBaseY}
        />
      </div>
      {node}
    </div>
  );
}
